import { useCallback, useRef } from "react";
import type { useRequestScope } from "./useRequestScope";

/** A synchronous lock prevents two clicks from starting the same write before React rerenders. */
export function useExclusiveAction<Args extends unknown[], Result>(
  scope: ReturnType<typeof useRequestScope>,
  action: (...args: Args) => Promise<Result>
) {
  const pending = useRef<(() => boolean) | null>(null);
  return useCallback(async (...args: Args) => {
    if (pending.current?.()) return;
    const isCurrent = scope.capture();
    if (!isCurrent()) return;
    pending.current = isCurrent;
    try {
      return await action(...args);
    } finally {
      if (pending.current === isCurrent) pending.current = null;
    }
  }, [action, scope]);
}
